import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * מכירות, יעדים ושיאים (PRD 5.18).
 *
 * המכירות נכנסות ידנית עד שקומקס יחובר (שלב 10), ולכן ההזנה היא
 * סיכום יומי לסניף או לעובד. שיא וספי יעד נבדקים בשרת ברגע הכניסה,
 * ולא בפונקציה מתוזמנת: הסף נחצה כשהמספר נכנס, וזה הרגע שההודעה
 * שווה בו משהו.
 */

export type MySales = {
  this_month: number;
  last_month: number;
  /** אותו חודש אשתקד. null = לא היו נתונים אז. */
  last_year: number | null;
  target: number | null;
  best_day: { date: string; amount: number } | null;
  best_month: { month: string; amount: number } | null;
};

export type BranchSales = {
  branch_id: string;
  branch_name: string;
  this_month: number;
  last_month: number;
  /** אותו חודש אשתקד. null = לא היו נתונים אז, וזה אינו ירידה. */
  last_year: number | null;
  target: number | null;
  best_month: number | null;
};

/** מכירות עובד/ת בחודש, מול היעד ומול התקופות הקודמות. */
export type EmployeeSalesRow = {
  sold: number;
  target: number | null;
  percent: number | null;
  last_month: number | null;
  last_year: number | null;
};

export type SalesRow = {
  id: string;
  branch_id: string;
  date: string;
  employee_id: string | null;
  amount: number;
  transactions: number | null;
  source: 'comax' | 'manual';
  branches: { name: string } | null;
  employees: { first_name: string; last_name: string } | null;
};

export type TargetsView = {
  month: string;
  branches: {
    branch_id: string;
    name: string;
    target: number | null;
    last_month: number | null;
    employees: { employee_id: string; name: string; target: number | null }[];
  }[];
};

export const SALES_KEY = ['sales'] as const;

export function useMySales() {
  return useQuery({
    queryKey: [...SALES_KEY, 'mine'],
    queryFn: async (): Promise<MySales | null> => {
      const { data, error } = await supabase.rpc('my_sales_summary');
      if (error) throw error;
      return data as MySales | null;
    },
  });
}

export function useBranchSales(month?: string) {
  return useQuery({
    queryKey: [...SALES_KEY, 'branches', month ?? 'current'],
    queryFn: async (): Promise<BranchSales[]> => {
      const { data, error } = await supabase.rpc('branches_sales_summary', {
        p_month: month ?? undefined,
      });
      if (error) throw error;
      return (data ?? []) as BranchSales[];
    },
    staleTime: 60 * 1000,
  });
}

/** השורות האחרונות שהוזנו, למסך ההזנה. ה RLS כבר מצמצם. */
export function useRecentSales(branchId: string | null) {
  return useQuery({
    queryKey: [...SALES_KEY, 'recent', branchId],
    enabled: Boolean(branchId),
    queryFn: async (): Promise<SalesRow[]> => {
      const { data, error } = await supabase
        .from('sales_daily')
        .select(
          `id, branch_id, date, employee_id, amount, transactions, source,
           branches ( name ),
           employees ( first_name, last_name )`,
        )
        .eq('branch_id', branchId as string)
        .order('date', { ascending: false })
        .limit(30);
      if (error) throw error;
      return (data ?? []) as unknown as SalesRow[];
    },
  });
}

export function useTargets(month: string) {
  return useQuery({
    queryKey: [...SALES_KEY, 'targets', month],
    queryFn: async (): Promise<TargetsView | null> => {
      const { data, error } = await supabase.rpc('sales_targets_for', { p_month: month });
      if (error) throw error;
      return data as TargetsView | null;
    },
  });
}

export function useSalesMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: SALES_KEY });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  /**
   * הזנה יומית. upsert, כי תיקון של יום שכבר הוזן הוא המקרה הרגיל:
   * מנהל מזין בערב, ולמחרת מגלה טעות.
   */
  const enter = useMutation({
    mutationFn: async (input: {
      branch_id: string;
      date: string;
      employee_id: string | null;
      amount: number;
      transactions: number | null;
      entered_by: string;
    }) => {
      const { error } = await supabase.from('sales_daily').upsert(
        { ...input, source: 'manual' },
        { onConflict: 'branch_id,date,employee_id' },
      );
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('sales_daily').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const setTarget = useMutation({
    mutationFn: async (input: {
      month: string;
      branch_id: string;
      employee_id: string | null;
      amount: number;
      created_by: string;
    }) => {
      const { error } = await supabase.from('sales_targets').upsert(input, {
        onConflict: 'month,branch_id,employee_id',
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const copyTargets = useMutation({
    mutationFn: async (input: { from: string; to: string }): Promise<number> => {
      const { data, error } = await supabase.rpc('copy_sales_targets', {
        p_from: input.from,
        p_to: input.to,
      });
      if (error) throw error;
      return (data as number | null) ?? 0;
    },
    onSuccess: refresh,
  });

  return { enter, remove, setTarget, copyTargets };
}

/** אחוז עמידה ביעד, בלי חלוקה באפס. */
export function targetPercent(actual: number, target: number | null): number | null {
  if (!target || target <= 0) return null;
  return Math.round((actual / target) * 100);
}

/** "עלייה של 12%" או "ירידה של 5%" מול החודש הקודם. */
/**
 * שינוי באחוזים מול תקופה אחרת, עם סימן.
 *
 * null כשאין מול מה להשוות: עובד או סניף שלא היו כאן לפני שנה
 * אינם "ירידה של 100 אחוז".
 */
export function changePercent(current: number, previous: number | null): number | null {
  if (previous === null || previous <= 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

export function trendLabel(current: number, previous: number): string | null {
  if (previous <= 0) return null;
  const change = Math.round(((current - previous) / previous) * 100);
  if (change === 0) return 'כמו בחודש שעבר';
  return change > 0 ? `עלייה של ${change}%` : `ירידה של ${Math.abs(change)}%`;
}

/** היום הראשון בחודש, בפורמט שה RPC מצפה לו. */
export function monthKey(date: Date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
}

/** מכירות לפי עובד/ת, למפתח לפי מזהה. */
export function useEmployeeSalesSummary() {
  return useQuery({
    queryKey: ['employees-sales-summary'],
    queryFn: async (): Promise<Record<string, EmployeeSalesRow>> => {
      const { data, error } = await supabase.rpc('employees_sales_summary');
      if (error) throw error;
      return (data ?? {}) as unknown as Record<string, EmployeeSalesRow>;
    },
  });
}
